/**
 * Iter-253 — Creator Network status banner shown on /talent/home.
 *
 * Status-aware:
 *   • not applied        → purple "Apply to the Creator Network" call-to-action
 *   • pending            → amber "Application under review" pill
 *   • changes_requested  → orange "Action needed" pill
 *   • approved           → emerald "Verified Creator ✓" badge with quick link
 *   • rejected           → grey "Re-apply when you're ready" pill
 *
 * Renders nothing while loading (no layout jank).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Sparkles, ArrowRight, CheckCircle2, Clock, AlertTriangle, XCircle,
} from 'lucide-react';


export default function CreatorNetworkBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/creator-applications/me')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const app = data?.application;
  const profile = data?.profile;
  const status = profile?.verified_creator ? 'approved' : app?.status;

  // Approved
  if (status === 'approved') {
    const ctLabel = {
      influencer: 'Influencer',
      content_creator: 'Content Creator',
      both: 'Influencer + Creator',
    }[profile?.creator_type] || 'Creator';
    return (
      <button
        onClick={() => navigate('/talent/creator-network')}
        className="w-full bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 hover:border-emerald-400 rounded-xl p-3 flex items-center gap-3 text-left transition-all"
        data-testid="creator-banner-approved"
      >
        <div className="w-9 h-9 rounded-full bg-emerald-500 text-white grid place-items-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-emerald-900">Verified {ctLabel} ✓</div>
          <div className="text-[11px] text-emerald-800/80">
            You&apos;re in the Creator Network. Tap to manage your profile.
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-emerald-700" />
      </button>
    );
  }

  // Pending
  if (status === 'pending') {
    return (
      <button
        onClick={() => navigate('/talent/creator-network')}
        className="w-full bg-amber-50 border border-amber-200 hover:border-amber-400 rounded-xl p-3 flex items-center gap-3 text-left transition-all"
        data-testid="creator-banner-pending"
      >
        <div className="w-9 h-9 rounded-full bg-amber-500 text-white grid place-items-center flex-shrink-0">
          <Clock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">Creator Network application under review</div>
          <div className="text-[11px] text-amber-800/80">
            We typically respond within 1–3 business days.
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-amber-700" />
      </button>
    );
  }

  // Changes requested
  if (status === 'changes_requested') {
    return (
      <button
        onClick={() => navigate('/talent/creator-network')}
        className="w-full bg-orange-50 border border-orange-200 hover:border-orange-400 rounded-xl p-3 flex items-center gap-3 text-left transition-all"
        data-testid="creator-banner-changes"
      >
        <div className="w-9 h-9 rounded-full bg-orange-500 text-white grid place-items-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-orange-900">Creator Network — action needed</div>
          <div className="text-[11px] text-orange-800/80">
            Your reviewer asked for a small update. Tap to view + re-submit.
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-orange-700" />
      </button>
    );
  }

  // Rejected → soft re-apply nudge
  if (status === 'rejected') {
    return (
      <button
        onClick={() => navigate('/talent/creator-network')}
        className="w-full bg-slate-50 border border-slate-200 hover:border-slate-400 rounded-xl p-3 flex items-center gap-3 text-left transition-all"
        data-testid="creator-banner-rejected"
      >
        <div className="w-9 h-9 rounded-full bg-slate-500 text-white grid place-items-center flex-shrink-0">
          <XCircle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-900">Creator Network — re-apply when you&apos;re ready</div>
          <div className="text-[11px] text-slate-600">
            Tap for the reviewer&apos;s notes or to re-submit.
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-500" />
      </button>
    );
  }

  // Default: marketing CTA
  return (
    <button
      onClick={() => navigate('/talent/creator-network')}
      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl p-3 flex items-center gap-3 text-left transition-all text-white shadow-sm"
      data-testid="creator-banner-cta"
    >
      <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center flex-shrink-0">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold">Become a verified iEvntür Creator</div>
        <div className="text-[11px] text-white/90">
          Apply to the Creator Network — Influencers + Content Creators
          welcome.
        </div>
      </div>
      <ArrowRight className="w-4 h-4" />
    </button>
  );
}
